import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Zap, Gift, Star, ArrowRight, RefreshCw, TrendingUp, Shield, Bot, BarChart3, Globe, Cpu } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const features = [
  { icon: Bot, title: 'Full Autopilot Mode', desc: 'Set it and forget it. 17 AI agents trade autonomously 24/7 — generating profit on BTC, ETH, SOL, S&P 500, NASDAQ, Gold, EUR/USD and 50+ instruments while you sleep.' },
  { icon: TrendingUp, title: '5 Asset Classes, One Engine', desc: 'Crypto, US Equities, ETFs, Forex, and IP Assets. Profit from Bitcoin rallies, stock momentum, currency swings, and commodity trends — all from a single dashboard.' },
  { icon: Shield, title: 'Institutional-Grade Risk', desc: 'Real-time drawdown controls, position sizing algorithms, and multi-layer risk management per agent. Your capital is protected at every level.' },
  { icon: BarChart3, title: 'Live P&L Tracking', desc: 'Watch your portfolio grow in real-time. Transparent performance dashboards with Alpha Score rankings and a competition arena for top agents.' },
  { icon: Globe, title: 'Agents-as-a-Service API', desc: 'Plug our AI agents into your own platform via REST API. Signals, webhooks, and delegated order execution — ready for enterprise.' },
  { icon: Cpu, title: '7-Phase Alpha Pipeline', desc: 'From signal detection to trade execution in under 60 seconds. Fully visualized, auditable, and optimized for maximum alpha extraction.' },
];

export default function ReferralLanding() {
  const { t } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const track = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/referrals/track/${code}`);
        setReferral(data);
        if (data.valid) {
          localStorage.setItem('referral_code', data.code);
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    track();
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030508] text-white overflow-x-hidden" data-testid="referral-landing">

      {/* Hero */}
      <section className="relative flex flex-col items-center justify-center text-center px-4 pt-20 pb-16">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.08)_0%,transparent_60%)]" />
        <div className="relative z-10 max-w-3xl mx-auto space-y-6">
          <div className="w-14 h-14 bg-white flex items-center justify-center mx-auto">
            <Zap className="w-8 h-8 text-[#030508]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>
            BlackOre<span className="text-emerald-400">X</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-xl mx-auto leading-relaxed">
            Fully autonomous. Fully on <span className="text-emerald-400 font-semibold">{t('referral.autopilot')}</span>. 
            17 AI agents generating profit across <span className="text-white font-semibold">BTC, ETH, S&P 500, NASDAQ, Gold, EUR/USD</span> and 50+ instruments — while you sleep.
          </p>

          {/* Stat bar */}
          <div className="flex flex-wrap items-center justify-center gap-6 pt-4">
            {[
              { value: '17', label: 'AI Agents' },
              { value: '50+', label: 'Instruments' },
              { value: '24/7', label: 'Autopilot' },
              { value: '<60s', label: 'Per Cycle' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl font-black text-emerald-400">{s.value}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-widest">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.title} className="bg-[#090B0F] border border-white/5 p-5 hover:border-emerald-500/20 transition-colors group">
              <f.icon className="w-5 h-5 text-emerald-500 mb-3 group-hover:text-emerald-400 transition-colors" />
              <h3 className="text-sm font-bold text-white mb-1">{f.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Autopilot Profit Banner */}
      <section className="max-w-5xl mx-auto px-4 pb-16">
        <div className="bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-500/20 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="bg-emerald-500/20 p-3 shrink-0">
              <Zap className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="space-y-2">
              <h2 className="text-base sm:text-lg font-bold text-white">{t('referral.autopilotProfit')}</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                Our multi-agent engine scans <span className="text-white font-medium">{t('referral.crypto')}</span> (BTC, ETH, SOL, BNB, XRP), 
                <span className="text-white font-medium"> {t('referral.usEquities')}</span> (AAPL, NVDA, TSLA, MSFT, AMZN), 
                <span className="text-white font-medium"> {t('referral.etfs')}</span> (SPY, QQQ, GLD), 
                <span className="text-white font-medium"> {t('referral.forexPairs')}</span> (EUR/USD, GBP/USD, USD/JPY), 
                and <span className="text-white font-medium"> {t('referral.indices')}</span> (S&P 500, NASDAQ, Dow Jones) every 60 seconds — 
                executing trades, capturing arbitrage, and compounding returns with zero manual intervention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Referral Card */}
      <section className="max-w-lg mx-auto px-4 pb-24">
        {referral?.valid ? (
          <div className="bg-[#090B0F] border border-emerald-500/30 p-8 space-y-5" data-testid="referral-valid">
            <div className="flex items-center gap-2 justify-center">
              <Gift className="w-6 h-6 text-emerald-400" />
              <h2 className="text-lg font-bold text-white">
                You've been invited by <span className="text-emerald-400">{referral.promoter_name}</span>
              </h2>
            </div>

            <p className="text-sm text-slate-400 text-center leading-relaxed">
              Join BlackOreX today and unlock exclusive onboarding rewards — courtesy of your referrer.
            </p>

            <div className="bg-[#11141D] border border-white/10 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Star className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="text-sm text-white font-medium">${referral.benefits?.credit} trading credit — yours instantly</span>
              </div>
              <div className="flex items-center gap-3">
                <Star className="w-4 h-4 text-purple-400 shrink-0" />
                <span className="text-sm text-white font-medium">{referral.benefits?.premium_days} days of Premium access — full agent suite</span>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 text-center">
              Your code <code className="text-emerald-400 font-mono">{referral.code}</code> will be applied automatically at registration.
            </p>

            <button
              onClick={() => navigate(`/register?ref=${referral.code}`)}
              className="w-full bg-emerald-500 text-black py-3.5 font-bold text-sm hover:bg-emerald-400 transition-colors flex items-center justify-center gap-2"
              data-testid="referral-register-btn"
            >
              GET STARTED NOW <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="bg-[#090B0F] border border-white/10 p-8 text-center space-y-4" data-testid="referral-invalid">
            <p className="text-slate-400 text-sm">{t('referral.invalidCode')}</p>
            <button
              onClick={() => navigate('/register')}
              className="bg-white/10 text-white px-8 py-2.5 font-mono text-sm hover:bg-white/20 transition-colors"
              data-testid="referral-goto-register-btn"
            >
              Go to Registration
            </button>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6 text-center">
        <p className="text-[11px] text-slate-600 tracking-wide">
          BlackOreX &copy; {new Date().getFullYear()} &mdash; AI-Powered Algorithmic Trading. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
